import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseMarkdownIntoSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage user permissions versions
 * Stores and tracks different versions of user permissions from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useUserPermissionsVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for user permissions version management
  const permissionsConfig = {
    // Determine if a message contains user permissions
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      // Check for user permissions specific markers
      return content.includes('## ') && (
        content.toLowerCase().includes('permission') ||
        content.toLowerCase().includes('role') ||
        content.toLowerCase().includes('access control') ||
        content.toLowerCase().includes('authorization')
      );
    },

    // Parse user permissions from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseMarkdownIntoSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedSections, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        sections: parsedSections,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(permissionsConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      sections: versionData.currentVersion.parsedContent || versionData.currentVersion.sections
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to user permissions-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useUserPermissionsVersions;
